import React, { useCallback, useContext, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { LinkButton, LinkIconButton } from '@atlaskit/button/new';
import { Inline } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';

import { firedAt } from '../../analytics';
import { useFireCrossAppProfileAnalytic } from '../../hooks/useFireCrossAppProfileAnalytic';
import { useUserProfileLink } from '../../hooks/useUserProfileLink';
import { useCardOpenTimer } from '../../store/cardOpenTimer';
import { UserProfileCardContext } from '../../store/context';
import type { PersonProfileData, UserProfileCardAction } from '../../types';
import { getIconForExternalMessageIntegration } from '../utils/externalMessageIntegrationIcons';
import { resolveExternalMessageFromIntegrations } from '../utils/thirdPartyIntegrationsByProduct';

import { ActionMenu, type ActionMenuItem } from './ActionMenu';
import { useKudosMenuItem } from './useKudosMenuItem';

const messages = defineMessages({
	viewProfile: {
		id: 'user-profile-card.action-bar.view-profile',
		defaultMessage: 'View profile',
		description: 'Button label to navigate to a user profile from the profile card',
	},
	sendMessageLabel: {
		id: 'user-profile-card.action-bar.send-message-label',
		defaultMessage: 'Message',
		description:
			'Accessible label for the send message button (e.g. Slack or Microsoft Teams) on the profile card',
	},
});

interface HeaderActionsProps {
	profileData: PersonProfileData;
	cloudId: string;
	/**
	 * Consumer-supplied actions rendered inside the action menu in the top-right
	 * of the header. Each action becomes a menu item; entries whose `shouldRender(profileData)`
	 * returns `false` are filtered out.
	 */
	actions?: UserProfileCardAction[];
	/**
	 * When `true` the user's account is deactivated: only the "View profile" button is rendered.
	 * The third-party message button, custom action menu, and kudos entry are all suppressed.
	 */
	isDeactivated?: boolean;
	/** Identity of the viewer. Used to suppress the kudos entry when viewing one's own profile. */
	viewerId?: string;
}

/**
 * Primary actions for the profile card header (org entry + view profile in Teams) plus an action
 * menu in the top-right that exposes consumer-supplied custom actions and a "Give kudos" entry
 * (always on, with self-hover suppression).
 */
export function HeaderActions({
	profileData,
	cloudId,
	actions,
	viewerId,
	isDeactivated = false,
}: HeaderActionsProps): React.JSX.Element | null {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const { getDurationSinceInteraction } = useCardOpenTimer();
	const { townsquareApp, onProfileClick, getTestId } = useContext(UserProfileCardContext);
	const { accountId, thirdPartyIntegrations, email } = profileData;
	const { href, target, onNavigate, isCrossAppMauGateEnabled } = useUserProfileLink(
		accountId,
		cloudId,
		townsquareApp,
	);
	const fireCrossAppProfileAnalytic = useFireCrossAppProfileAnalytic(townsquareApp);

	const externalMessage = useMemo(
		() => resolveExternalMessageFromIntegrations(thirdPartyIntegrations, email),
		[email, thirdPartyIntegrations],
	);

	const handleThirdPartyMessageClick = useCallback(() => {
		// Reuse the existing `@atlaskit/profilecard` clicked.action event so the SLO dashboards
		// see a single click-event surface for both profile-card packages. `index` and `hasOnClick`
		// are required by that schema but are not meaningful for user-profile-card actions, so we
		// always send `index: 0` and `hasOnClick: true`.
		fireEvent('ui.profilecard.clicked.action', {
			firedAt: firedAt(),
			duration: getDurationSinceInteraction() ?? 0,
			hasHref: Boolean(externalMessage?.dm.openDmUrl),
			hasOnClick: true,
			index: 0,
			actionId: 'thirdPartyMessage',
		});
	}, [fireEvent, getDurationSinceInteraction, externalMessage]);

	const handleViewProfileClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			fireEvent('ui.profilecard.clicked.action', {
				firedAt: firedAt(),
				duration: getDurationSinceInteraction() ?? 0,
				hasHref: Boolean(href),
				hasOnClick: true,
				index: 0,
				actionId: 'viewProfile',
			});
			if (isCrossAppMauGateEnabled) {
				fireCrossAppProfileAnalytic();
			}
			onProfileClick?.(accountId);
			onNavigate?.(event);
		},
		[
			fireEvent,
			getDurationSinceInteraction,
			href,
			onNavigate,
			onProfileClick,
			isCrossAppMauGateEnabled,
			fireCrossAppProfileAnalytic,
			accountId,
		],
	);

	const menuItems = useMemo<ActionMenuItem[]>(() => {
		const visible = (actions ?? []).filter(
			(action) => !action.shouldRender || action.shouldRender(profileData),
		);

		return visible.map((action, index) => {
			const id = action.id ?? `action-${index}`;
			if (action.link) {
				return {
					kind: 'link',
					id,
					label: action.label,
					href: action.link,
					target: action.target,
					onClick: action.callback
						? (event: React.MouseEvent | React.KeyboardEvent, ...args: any[]) => {
								const isBasicClick = !(
									(event as React.MouseEvent).metaKey ||
									(event as React.MouseEvent).altKey ||
									(event as React.MouseEvent).ctrlKey ||
									(event as React.MouseEvent).shiftKey
								);
								if (isBasicClick) {
									event.preventDefault();
									action.callback?.(event, ...args);
								}
							}
						: undefined,
				};
			}
			return {
				kind: 'button',
				id,
				label: action.label,
				onClick: (event: React.MouseEvent | React.KeyboardEvent, ...args: any[]) =>
					action.callback?.(event, ...args),
			};
		});
	}, [actions, profileData]);

	const { menuItem: kudosMenuItem, launcher: kudosLauncher } = useKudosMenuItem({
		accountId,
		cloudId,
		viewerId,
	});

	const allMenuItems = useMemo<ActionMenuItem[]>(
		// Deactivated accounts only surface the "View profile" button - no custom actions or kudos.
		() => (isDeactivated ? [] : kudosMenuItem ? [...menuItems, kudosMenuItem] : menuItems),
		[isDeactivated, menuItems, kudosMenuItem],
	);

	const hasActionMenu = allMenuItems.length > 0;

	// Without an accountId there is nothing to show. We allow rendering when there is no profile
	// link as long as the action menu has content - so consumers using the card without Teams
	// navigation can still surface custom actions / kudos.
	if (!accountId || (!href && !hasActionMenu)) {
		return null;
	}

	const inline = (
		<Inline space="space.100" alignBlock="center">
			{!isDeactivated && externalMessage && (
				<LinkIconButton
					href={externalMessage.dm.openDmUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleThirdPartyMessageClick}
					icon={getIconForExternalMessageIntegration(externalMessage.dm.integration)}
					label={intl.formatMessage(messages.sendMessageLabel)}
					isTooltipDisabled={false}
				/>
			)}
			{accountId && href && (
				<LinkButton
					href={href}
					appearance="default"
					target={target}
					onClick={handleViewProfileClick}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
					testId={getTestId('view-profile-button')}
				>
					{intl.formatMessage(messages.viewProfile)}
				</LinkButton>
			)}
			{hasActionMenu && <ActionMenu items={allMenuItems} name={profileData.name} />}
		</Inline>
	);

	// When there's no kudos launcher to portal-mount, return the bare `<Inline>` element so that
	// callers which expected the legacy single-element return tree continue to see one node (and
	// no extra fragment wrapper). The fragment form is only used when we actually need to render
	// the launcher sibling.
	if (isDeactivated || !kudosLauncher) {
		return inline;
	}

	return (
		<>
			{inline}
			{kudosLauncher}
		</>
	);
}
